import React, { useState } from 'react';

const ConsentForm = ({ username, apiBaseUrl, onConsentSubmitted }) => {
  const [fullname, setFullname] = useState('');
  const [signDate, setSignDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  // Get today's date in MM/DD/YYYY format
  const getTodayDate = () => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const year = today.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Handle scroll to check if user has scrolled to bottom
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 10;
    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  // Handle print functionality
  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    // Write the consent form content to the print window
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Research Consent Form</title>
        <style>
          body {
            font-family: 'Times New Roman', serif;
            font-size: 12pt;
            line-height: 1.6;
            margin: 1in;
            color: #000;
          }
          h1 {
            text-align: center;
            font-size: 16pt;
            font-weight: bold;
            margin-bottom: 10pt;
          }
          h2 {
            text-align: center;
            font-size: 14pt;
            font-weight: bold;
            margin-bottom: 20pt;
          }
          h3 {
            font-size: 12pt;
            font-weight: bold;
            margin-top: 15pt;
            margin-bottom: 5pt;
          }
          p {
            margin-bottom: 10pt;
            text-align: justify;
          }
          .signature-section {
            margin-top: 30pt;
            border: 2px solid #000;
            padding: 15pt;
          }
          .signature-line {
            border-bottom: 1px solid #000;
            margin: 20pt 0 5pt 0;
            height: 20pt;
          }
          .date-line {
            border-bottom: 1px solid #000;
            margin: 20pt 0 5pt 0;
            height: 20pt;
            width: 200pt;
          }
          @media print {
            body { margin: 0.5in; }
          }
        </style>
      </head>
      <body>
        <h1>Investigating the effectiveness of AI-powered assistants for computer science education</h1>
        <h2>University of Illinois Chicago</h2>
        
        <h3>About this research study</h3>
        <p>You are being asked to participate in a research study. Research studies answer important questions that might help change or improve the way we do things in the future.</p>
        
        <h3>Taking part in this study is voluntary</h3>
        <p>Your participation in this research study is voluntary. You may choose to not take part in this study or may choose to leave the study at any time. Deciding not to participate, or deciding to leave the study later, will not result in any penalty or loss of benefits to which you are entitled and will not affect your relationship with the University of Illinois Hospital and Health Sciences System (UI Health) and/or University of Illinois at Chicago (UIC).</p>
        <p>This consent form will give you information about the research study to help you decide whether you want to participate. Please read this form and ask any questions you have before agreeing to be in the study.</p>
        <p>You are being asked to participate in this research study because you have enrolled in a course that we offer AI-assistant for it. Roughly 100-500 subjects will be enrolled in this research study over multiple courses.</p>
        
        <h3>Why is this research being done?</h3>
        <p>With the expansion of computer science programs, the student-to-teacher ratio is likely to rise if university resources can't keep pace due to physical constraints. To address this challenge, it is essential to explore advanced technologies like artificial intelligence to sustain and enhance educational quality. Our study aims to assess the effectiveness of an AI assistant in advancing computer science education.</p>
        
        <h3>What will I be asked to do during the study?</h3>
        <p>You will have access to the AI assistant, which offers feedback to help you enhance your coursework. Additionally, you can utilize its chatbot for questions and answers related to course materials. After submitting each piece of coursework, you will be asked to complete a Google Form survey to evaluate the feedback you received. You are welcome to provide more detailed comments on the quality of the AI feedback. Furthermore, you can rate the AI's responses directly within the chatbot interface. Filling each questioner will take less than 5 minutes.</p>
        
        <h3>What about privacy and confidentiality?</h3>
        <p>For our study, we will use your anonymized submitted coursework and your conversations with the AI chatbot, along with the AI's feedback and responses. After data collection, all direct identifiers will be removed from the data. When the results of the study are published or presented at conferences, your identity will remain confidential, as no one will be able to ascertain your participation or identify you based on your submitted coursework. Your privacy is safeguarded by recording only the data and not any identifiable information about participants.</p>
        
        <h3>What will happen with my information used in this study?</h3>
        <p>The information collected for this study may be used for future research studies and/or shared with other researchers in the future. If this happens, information which could identify you will be removed before any information is shared. Once the identifying information is removed, the information cannot be withdrawn from further use. You will not be asked for additional consent.</p>
        
        <h3>What are the main risks of the study?</h3>
        <p>In some instances, the AI assistant may provide feedback or responses that are misleading or more confusing than clarifying. Another possible risk of the study is that your participation in the study or information about your coursework submissions might become known to individuals outside the study.</p>
        
        <h3>What are the benefits of the study?</h3>
        <p>You will have 24/7 access to the AI assistant, which can provide you with timely assistance to enhance your submissions. This may also reduce the necessity for attending office hours to seek guidance from teaching staff.</p>
        
        <h3>What are the costs for participating in this research?</h3>
        <p>We are using commercial services for providing AI-assistant but there are no costs to you for participating in this research.</p>
        
        <h3>Will I be reimbursed for any of my expenses or paid for my participation in this research?</h3>
        <p>You will not be offered payment for being in this study.</p>
        
        <h3>Can I withdraw or be removed from the study?</h3>
        <p>If you decide not to participate, you have the right to withdraw your consent and leave the study at any time without penalty. We also have the right to stop your participation in this study without your consent.</p>
        
        <h3>What if I am a UIC student?</h3>
        <p>You may choose not to participate or to stop your participation in this research at any time. This will not affect your class standing or grades at UIC. The investigator may also end your participation in the research. If this happens, your class standing or grades will not be affected. You will not be offered or receive any special consideration if you participate in this research.</p>
        
        <h3>Questions about the study?</h3>
        <p>For questions, concerns, or complaints about the study, please contact Dr. Sara Riazi via email at riazi@uic.edu.</p>
        <p>If you have questions about your rights as a study subject, including questions, concerns, or complaints, or if you feel you have not been treated according to the description in this form, or to offer input you may call the UIC Office for the Protection of Research Subjects (OPRS) at 312-996-1711 or email OPRS at uicirb@uic.edu</p>
        
        <h3>Remember:</h3>
        <p>Your participation in this research study is voluntary. Your decision whether to participate will not affect your current or future relations with the University. If you decide to participate, you are free to withdraw at any time without affecting that relationship.</p>
        
        <h3>Minor subjects:</h3>
        <p>I certify that I am over 18 at the time submitting this consent form. Minor subjects may use AI assistant without opt-in in this study.</p>
        
        <div class="signature-section">
          <h3>Consent of Subject:</h3>
          <p>I have read the above information. I have been given an opportunity to contact the researchers and ask questions, and my questions have been answered to my satisfaction. I agree to participate in this research. <strong>PLEASE PRINT OUT A COPY OF THIS DOCUMENT FOR YOUR RECORDS.</strong></p>
          
          <p style="margin-top: 30pt;"><strong>Participant's Full Name:</strong></p>
          <div class="signature-line"></div>
          
          <p style="margin-top: 20pt;"><strong>Participant's Signature:</strong></p>
          <div class="signature-line"></div>
          
          <p style="margin-top: 20pt;"><strong>Date:</strong></p>
          <div class="date-line"></div>
        </div>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    
    // Wait for content to load, then print
    printWindow.onload = function() {
      printWindow.print();
      printWindow.close();
    };
  };

  const handleSubmit = async () => {
    if (!fullname.trim()) {
      setError('Please enter your full name.');
      return;
    }
    
    if (!signDate.trim()) {
      setError('Please enter the date.');
      return;
    }
    
    if (!hasScrolledToBottom) {
      setError('Please read the entire consent form by scrolling to the bottom before submitting.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${apiBaseUrl}/consent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          username: username,
          fullname: fullname.trim(),
          signDate: signDate.trim()
        })
      });

      if (response.ok) {
        // Call the callback to notify parent component
        if (onConsentSubmitted) {
          onConsentSubmitted();
        }
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to submit consent form');
      }
    } catch (error) {
      console.error('Error submitting consent:', error);
      setError('Failed to submit consent form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  React.useEffect(() => {
    // Set today's date as default
    setSignDate(getTodayDate());
  }, []);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-4">Research Consent Form</h1>
            <p className="text-gray-600">
              Please read the following consent form carefully and provide your consent to participate in this research study.
            </p>
          </div>
          <div className="flex-shrink-0">
            <button
              onClick={handlePrint}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md flex items-center transition-colors duration-200"
            >
              <span className="mr-2">🖨️</span>
              Print Document
            </button>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 no-print">
          <div className="flex">
            <span className="text-red-500 text-xl mr-3">⚠️</span>
            <div>
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Consent Form Content */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden print-full-width">
        <div className="bg-blue-50 px-6 py-4 border-b border-blue-200">
          <h2 className="text-xl font-semibold text-blue-800">
            Investigating the effectiveness of AI-powered assistants for computer science education
          </h2>
          <p className="text-blue-700 mt-1">University of Illinois Chicago</p>
        </div>

        {/* Scrollable Content */}
        <div 
          className="p-6 max-h-96 overflow-y-auto border-2 border-gray-200 print-content"
          onScroll={handleScroll}
        >
          <div className="prose max-w-none text-sm leading-relaxed space-y-4">
            
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">About this research study</h3>
              <p className="text-gray-700">
                You are being asked to participate in a research study. Research studies answer important questions that might help change or improve the way we do things in the future.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Taking part in this study is voluntary</h3>
              <p className="text-gray-700">
                Your participation in this research study is voluntary. You may choose to not take part in this study or may choose to leave the study at any time. Deciding not to participate, or deciding to leave the study later, will not result in any penalty or loss of benefits to which you are entitled and will not affect your relationship with the University of Illinois Hospital and Health Sciences System (UI Health) and/or University of Illinois at Chicago (UIC).
              </p>
              <p className="text-gray-700 mt-2">
                This consent form will give you information about the research study to help you decide whether you want to participate. Please read this form and ask any questions you have before agreeing to be in the study.
              </p>
              <p className="text-gray-700 mt-2">
                You are being asked to participate in this research study because you have enrolled in a course that we offer AI-assistant for it. Roughly 100-500 subjects will be enrolled in this research study over multiple courses.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Why is this research being done?</h3>
              <p className="text-gray-700">
                With the expansion of computer science programs, the student-to-teacher ratio is likely to rise if university resources can't keep pace due to physical constraints. To address this challenge, it is essential to explore advanced technologies like artificial intelligence to sustain and enhance educational quality. Our study aims to assess the effectiveness of an AI assistant in advancing computer science education.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">What will I be asked to do during the study?</h3>
              <p className="text-gray-700">
                You will have access to the AI assistant, which offers feedback to help you enhance your coursework. Additionally, you can utilize its chatbot for questions and answers related to course materials. After submitting each piece of coursework, you will be asked to complete a Google Form survey to evaluate the feedback you received. You are welcome to provide more detailed comments on the quality of the AI feedback. Furthermore, you can rate the AI's responses directly within the chatbot interface. Filling each questioner will take less than 5 minutes.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">What about privacy and confidentiality?</h3>
              <p className="text-gray-700">
                For our study, we will use your anonymized submitted coursework and your conversations with the AI chatbot, along with the AI's feedback and responses. After data collection, all direct identifiers will be removed from the data. When the results of the study are published or presented at conferences, your identity will remain confidential, as no one will be able to ascertain your participation or identify you based on your submitted coursework. Your privacy is safeguarded by recording only the data and not any identifiable information about participants.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">What will happen with my information used in this study?</h3>
              <p className="text-gray-700">
                The information collected for this study may be used for future research studies and/or shared with other researchers in the future. If this happens, information which could identify you will be removed before any information is shared. Once the identifying information is removed, the information cannot be withdrawn from further use. You will not be asked for additional consent.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">What are the main risks of the study?</h3>
              <p className="text-gray-700">
                In some instances, the AI assistant may provide feedback or responses that are misleading or more confusing than clarifying. Another possible risk of the study is that your participation in the study or information about your coursework submissions might become known to individuals outside the study.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">What are the benefits of the study?</h3>
              <p className="text-gray-700">
                You will have 24/7 access to the AI assistant, which can provide you with timely assistance to enhance your submissions. This may also reduce the necessity for attending office hours to seek guidance from teaching staff.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">What are the costs for participating in this research?</h3>
              <p className="text-gray-700">
                We are using commercial services for providing AI-assistant but there are no costs to you for participating in this research.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Will I be reimbursed for any of my expenses or paid for my participation in this research?</h3>
              <p className="text-gray-700">
                You will not be offered payment for being in this study.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Can I withdraw or be removed from the study?</h3>
              <p className="text-gray-700">
                If you decide not to participate, you have the right to withdraw your consent and leave the study at any time without penalty. We also have the right to stop your participation in this study without your consent.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">What if I am a UIC student?</h3>
              <p className="text-gray-700">
                You may choose not to participate or to stop your participation in this research at any time. This will not affect your class standing or grades at UIC. The investigator may also end your participation in the research. If this happens, your class standing or grades will not be affected. You will not be offered or receive any special consideration if you participate in this research.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Questions about the study?</h3>
              <p className="text-gray-700">
                For questions, concerns, or complaints about the study, please contact Dr. Sara Riazi via email at riazi@uic.edu.
              </p>
              <p className="text-gray-700 mt-2">
                If you have questions about your rights as a study subject, including questions, concerns, or complaints, or if you feel you have not been treated according to the description in this form, or to offer input you may call the UIC Office for the Protection of Research Subjects (OPRS) at 312-996-1711 or email OPRS at uicirb@uic.edu
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Remember:</h3>
              <p className="text-gray-700">
                Your participation in this research study is voluntary. Your decision whether to participate will not affect your current or future relations with the University. If you decide to participate, you are free to withdraw at any time without affecting that relationship.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Minor subjects:</h3>
              <p className="text-gray-700">
                I certify that I am over 18 at the time submitting this consent form. Minor subjects may use AI assistant without opt-in in this study.
              </p>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">Consent of Subject:</h3>
              <p className="text-blue-700">
                I have read the above information. I have been given an opportunity to contact the researchers and ask questions, and my questions have been answered to my satisfaction. I agree to participate in this research. PLEASE PRINT OUT A COPY OF THIS DOCUMENT FOR YOUR RECORDS.
              </p>
            </div>

          </div>
        </div>

        {/* Scroll indicator */}
        {!hasScrolledToBottom && (
          <div className="bg-yellow-50 border-t border-yellow-200 px-6 py-3 no-print">
            <p className="text-yellow-700 text-sm flex items-center">
              <span className="text-yellow-500 mr-2">⬇️</span>
              Please scroll down to read the entire consent form before submitting.
            </p>
          </div>
        )}

        {/* Form Section */}
        <div className="border-t border-gray-200 p-6 bg-gray-50 no-print">
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name: <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={fullname}
                  onChange={(e) => setFullname(e.target.value)}
                  placeholder="Enter your full legal name"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date: <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={signDate}
                  onChange={(e) => setSignDate(e.target.value)}
                  placeholder="MM/DD/YYYY"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 no-print">
              <p className="text-blue-700 text-sm mb-3">
                By submitting this form, I confirm that:
              </p>
              <ul className="text-blue-700 text-sm space-y-1 ml-4">
                <li>• I am over 18 years of age</li>
                <li>• I have read and understood the entire consent form</li>
                <li>• I voluntarily agree to participate in this research study</li>
                <li>• I understand I can withdraw from the study at any time</li>
              </ul>
            </div>

            <div className="flex justify-end space-x-4 no-print">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !hasScrolledToBottom}
                className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-8 py-3 rounded-md font-medium transition-colors duration-200 flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  'Submit Consent Form'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentForm;